import { UCAT_SUBTEST_KEYS } from './ucatSubtests';

/**
 * Topic papers on the Practice Topics pages. A topic paper is one file of practice
 * questions, published by the content importer as its own one-part set. The server lists
 * them (GET /exams/<exam>/topic-papers); this module only groups them for the page:
 * by `section` (the paper's own section code) and by `level` (the Practice Topics
 * page: Beginnings, Foundations, Beyond; no level means Beginnings).
 */

export const TOPIC_PAPERS_FALLBACK_LABEL = 'Practice Topics';

export const TOPIC_TIER_ORDER = ['beginnings', 'foundations', 'beyond'];

export const TOPIC_TIER_LABELS = {
  beginnings: 'Beginnings',
  foundations: 'Foundations',
  beyond: 'Beyond',
};

/** Section code -> the label a section's heading shows, per exam (upper-case name). */
export const TOPIC_SECTION_LABELS = {
  UCAT: {
    s1: 'Verbal Reasoning',
    s2: 'Decision Making',
    s3: 'Quantitative Reasoning',
    s4: 'Situational Judgement',
  },
  TARA: {
    s1: 'Critical Thinking',
    s2: 'Problem Solving',
    s3: 'Writing Task',
  },
  LNAT: {
    sA: 'Section A',
    sB: 'Section B',
  },
};

const UCAT_SECTION_TO_MODULE = {
  s1: 'verbal_reasoning',
  s2: 'decision_making',
  s3: 'quantitative_reasoning',
  s4: 'situational_judgement',
};

const TARA_SECTION_TO_MODULE = {
  s1: 'critical_thinking',
  s2: 'problem_solving',
  s3: 'writing',
};

/** The UCAT subtest a topic paper's section code belongs to, or null when it has none. */
export function ucatModuleKeyForSection(section) {
  const key = UCAT_SECTION_TO_MODULE[String(section || '')];
  return key && UCAT_SUBTEST_KEYS.includes(key) ? key : null;
}

/** The TARA module a topic paper's section code belongs to, or null when it has none. */
export function taraModuleKeyForSection(section) {
  return TARA_SECTION_TO_MODULE[String(section || '')] || null;
}

function tierOf(paper) {
  const level = String(paper?.level || '').toLowerCase();
  return TOPIC_TIER_ORDER.includes(level) ? level : 'beginnings';
}

function byDisplayOrder(a, b) {
  const orderA = Number.isFinite(Number(a?.display_order)) ? Number(a.display_order) : 0;
  const orderB = Number.isFinite(Number(b?.display_order)) ? Number(b.display_order) : 0;
  if (orderA !== orderB) return orderA - orderB;
  return String(a?.paper_id || '').localeCompare(String(b?.paper_id || ''), undefined, { numeric: true });
}

/**
 * [{ section, label, tiers: [{ tier, label, params }] }]. Sections come in the exam's
 * label order, then any other or missing section as 'Practice Topics'. `params` are the
 * topic paper objects of that section and tier, in display order. Empty input gives [].
 */
export function buildTopicPaperSections(examName, papers) {
  const labels = TOPIC_SECTION_LABELS[String(examName || '').toUpperCase()] || {};
  const order = Object.keys(labels);
  const groups = new Map();
  (papers || []).forEach((paper) => {
    if (!paper?.paper_id) return;
    const code = Object.prototype.hasOwnProperty.call(labels, paper.section) ? paper.section : '';
    if (!groups.has(code)) groups.set(code, []);
    groups.get(code).push(paper);
  });
  const codes = [...order.filter((code) => groups.has(code)), ...(groups.has('') ? [''] : [])];
  return codes.map((code) => {
    const inSection = groups.get(code).slice().sort(byDisplayOrder);
    const tiers = TOPIC_TIER_ORDER
      .map((tier) => ({
        tier,
        label: TOPIC_TIER_LABELS[tier],
        params: inSection.filter((paper) => tierOf(paper) === tier),
      }))
      .filter((block) => block.params.length > 0);
    return {
      section: code || 'other',
      label: code ? labels[code] : TOPIC_PAPERS_FALLBACK_LABEL,
      tiers,
    };
  });
}

/** The topic papers of one Practice Topics page (tier), in display order. */
export function topicPapersForTier(papers, tier) {
  return (papers || [])
    .filter((paper) => paper?.paper_id && tierOf(paper) === tier)
    .slice()
    .sort(byDisplayOrder);
}
